import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";
import { Header, Footer } from "../../components/Orchestrio";

const MobileAppsPage = () => {
  return (
    <div className="min-h-screen bg-[#1E1E1E]" data-testid="mobile-apps-service-page">
      <Header />

      {/* Header */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <Link to="/services" className="text-[#5F8FB8] text-sm hover:underline mb-6 inline-block">
            ← Back to Services
          </Link>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            Mobile Applications
          </h1>
          <p className="text-xl text-[#5F8FB8] mb-4">
            MVP to production
          </p>
          <p className="text-lg text-[#9CA3AF] max-w-2xl leading-relaxed">
            Clear scope, realistic delivery.
          </p>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-16 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-6">The problem we solve</h2>
          <div className="max-w-2xl">
            <p className="text-[#9CA3AF] leading-relaxed mb-4">
              Mobile app development has a reputation for going over budget and over time. 
              Features creep in. Scope expands. Timelines slip.
            </p>
            <p className="text-[#9CA3AF] leading-relaxed mb-4">
              Many projects start with big ambitions and end with exhausted budgets 
              and half-finished apps that never launch.
            </p>
            <p className="text-[#9CA3AF] leading-relaxed">
              You need a team that will help you define what actually matters, 
              build it properly, and ship it on time.
            </p>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-16 bg-[#242424] border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-8">How we approach it</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold mb-2">We define the MVP ruthlessly</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                What's the smallest thing you can ship that delivers real value? 
                We help you cut scope to what actually matters for launch.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">We give you honest timelines</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                Mobile apps take longer than people think. We provide realistic estimates 
                based on actual complexity, not optimistic guesses.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">We build cross-platform when it makes sense</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                Not every app needs to be native on both platforms. 
                We'll recommend the approach that fits your budget and requirements.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">We plan for app store approval</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                App stores have requirements that can delay launch. 
                We build with these in mind from the start.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-16 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-8">What you can expect</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 bg-[#242424] border border-[#2E2E2E]">
              <h3 className="font-semibold mb-4">Deliverables</h3>
              <ul className="space-y-3">
                {[
                  "MVP scoping & specification",
                  "UI/UX design",
                  "iOS and/or Android development",
                  "Backend API (if needed)",
                  "User authentication",
                  "Push notifications",
                  "App store submission",
                  "Documentation",
                  "Post-launch support period"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                    <CheckCircle className="w-4 h-4 text-[#5F8FB8] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-6 bg-[#242424] border border-[#2E2E2E]">
              <h3 className="font-semibold mb-4">Timeline & Investment</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-[#6B7280] mb-1">Typical timeline</p>
                  <p className="font-semibold">12–24 weeks</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280] mb-1">Investment range</p>
                  <p className="font-semibold">£20,000 – £80,000+ +VAT</p>
                </div>
                <div className="pt-4 border-t border-[#2E2E2E]">
                  <p className="text-xs text-[#6B7280]">
                    Price varies significantly based on complexity, platforms (iOS/Android/both), 
                    and backend requirements. MVP projects start at the lower end.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MVP vs Full App */}
      <section className="py-16 bg-[#242424] border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-6">MVP vs. Full Product</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 bg-[#1E1E1E] border border-[#2E2E2E]">
              <h3 className="font-semibold mb-3 text-[#5F8FB8]">MVP (£20k–£40k)</h3>
              <p className="text-sm text-[#9CA3AF] mb-4">
                Core functionality only. Single platform. Prove the concept before investing more.
              </p>
              <p className="text-xs text-[#6B7280]">Timeline: 12–16 weeks</p>
            </div>
            <div className="p-6 bg-[#1E1E1E] border border-[#2E2E2E]">
              <h3 className="font-semibold mb-3 text-[#5F8FB8]">Full Product (£50k+)</h3>
              <p className="text-sm text-[#9CA3AF] mb-4">
                Complete feature set. Both platforms. Admin tools. Analytics. Ready to scale.
              </p>
              <p className="text-xs text-[#6B7280]">Timeline: 20–30+ weeks</p>
            </div>
          </div>
        </div>
      </section>

      {/* Best For */}
      <section className="py-16 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-6">This is right for you if...</h2>
          <ul className="space-y-3 max-w-2xl">
            {[
              "You have a clear idea of what problem the app solves",
              "You're willing to launch with core features and iterate",
              "You have budget for both development AND post-launch improvements",
              "You understand that good apps take time to build properly"
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-[#9CA3AF]">
                <span className="text-[#5F8FB8]">→</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#242424] border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
            <div>
              <h2 className="text-2xl font-bold mb-3">Have a mobile app idea?</h2>
              <p className="text-[#9CA3AF] max-w-lg">
                Get in touch and tell us what you're trying to build. 
                We'll help you define scope and give you an honest assessment.
              </p>
            </div>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2 flex-shrink-0">
              Get in touch <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MobileAppsPage;
